import { useEffect, useState } from "react";
import posthog from "posthog-js";
import { Wifi, Loader2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Mascot } from "@/components/Mascot";
import { useWifiSpeedTest as useWifiSpeedTestHook } from "@/hooks/useWifiSpeedTest";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useReviewWinMoment } from "@/components/AppReviewPromptProvider";

interface WifiSpeedTestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cafeId: string;
  cafeName: string;
  checkInId?: string | null;
  source?: "detail" | "checkin_confirm" | "push" | "other";
}

type Phase = "checking" | "intro" | "running" | "result" | "saved" | "already_tested";

export function WifiSpeedTestDialog({
  open,
  onOpenChange,
  cafeId,
  cafeName,
  source = "other",
}: WifiSpeedTestDialogProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { notifyWinMoment } = useReviewWinMoment();
  const { isRunning, progress, result, error, runSpeedTest, reset } = useWifiSpeedTestHook();
  const [phase, setPhase] = useState<Phase>("checking");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPhase("checking");
    reset();
    let cancelled = false;
    (async () => {
      if (!user) {
        if (!cancelled) setPhase("intro");
        return;
      }
      const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();
      const { data, error: qErr } = await supabase
        .from("wifi_speed_tests")
        .select("id")
        .eq("user_id", user.id)
        .eq("cafe_id", cafeId)
        .gte("created_at", twoHoursAgo)
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (qErr) {
        console.error("[WifiSpeedTestDialog] recent check error:", qErr);
        setPhase("intro");
        return;
      }
      setPhase(data ? "already_tested" : "intro");
      if (data) {
        // Fire-and-forget: cancel any pending wifi_followup push for this cafe.
        // Non-blocking; never fail the UI on error.
        try {
          supabase.functions
            .invoke("cancel-scheduled-pushes", { body: { cafe_id: cafeId } })
            .catch((err) => console.log("[WifiSpeedTestDialog] cancel push (already_tested) failed:", err));
        } catch (err) {
          console.log("[WifiSpeedTestDialog] cancel push (already_tested) threw:", err);
        }
      }

    })();
    return () => {
      cancelled = true;
    };
  }, [open, cafeId, user, reset]);

  const handleStart = async () => {
    try {
      posthog.capture("wifi_speed_test_started", { cafe_id: cafeId, source });
    } catch { /* ignore */ }
    setPhase("running");
    const r = await runSpeedTest();
    if (!r) {
      // error state handled by hook
      setPhase("intro");
      return;
    }
    setPhase("result");
    // auto-save
    await persist(r.downloadSpeed, r.latency);
  };

  const persist = async (speedMbps: number, latencyMs: number | null) => {
    if (!user) {
      toast({ title: "Sign in required", description: "Sign in to save speed test results.", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const { error: insErr } = await supabase.from("wifi_speed_tests").insert({
        cafe_id: cafeId,
        user_id: user.id,
        speed_mbps: speedMbps,
        latency_ms: latencyMs ?? null,
      });
      if (insErr) throw insErr;
      try {
        posthog.capture("wifi_speed_test_completed", { cafe_id: cafeId, speed_mbps: speedMbps, source });
      } catch { /* ignore */ }
      notifyWinMoment();


      // Fire-and-forget: cancel any pending wifi_followup push for this cafe.
      // Must NOT block the "saved" state or fail the save if this errors/hangs.
      try {
        supabase.functions
          .invoke("cancel-scheduled-pushes", { body: { cafe_id: cafeId } })
          .catch((err) => console.log("[WifiSpeedTestDialog] cancel push (saved) failed:", err));
      } catch (err) {
        console.log("[WifiSpeedTestDialog] cancel push (saved) threw:", err);
      }


      // Mirror the Mbps result to the user's active check-in as a 1-5 rating
      try {
        const mapped =
          speedMbps < 5 ? 1
          : speedMbps < 20 ? 2
          : speedMbps < 50 ? 3
          : speedMbps < 100 ? 4
          : 5;
        const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();
        const { data: activeCheckIn } = await supabase
          .from("check_ins")
          .select("id")
          .eq("user_id", user.id)
          .eq("cafe_id", cafeId)
          .gte("checked_in_at", twoHoursAgo)
          .order("checked_in_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (activeCheckIn?.id) {
          await supabase
            .from("check_ins")
            .update({ wifi_speed: mapped })
            .eq("id", activeCheckIn.id);
          queryClient.invalidateQueries({ queryKey: ["recent-cafe-conditions"] });
          // The mirror write can flip a skipped check-in into a counting visit,
          // so refresh streak / today / contribution caches.
          queryClient.invalidateQueries({ queryKey: ["user-streak"] });
          queryClient.invalidateQueries({ queryKey: ["user-checked-in-today", cafeId, user.id] });
          queryClient.invalidateQueries({ queryKey: ["user-cafe-contributions", cafeId, user.id] });
        }
      } catch (mirrorErr) {
        console.error("[WifiSpeedTestDialog] check-in mirror error:", mirrorErr);
      }

      // Detail cache is keyed by slug (["cafe", slug]) and no query uses
      // "cafe-detail" — invalidate the prefixes that actually back the
      // detail page and browse cards (DB trigger updates wifi_speed_avg).
      queryClient.invalidateQueries({ queryKey: ["cafe"] });
      queryClient.invalidateQueries({ queryKey: ["cafes"] });
      queryClient.invalidateQueries({ queryKey: ["cafe-checkins", cafeId] });
      queryClient.invalidateQueries({ queryKey: ["historical-vibes", cafeId] });
      setPhase("saved");
    } catch (e: any) {
      console.error("[WifiSpeedTestDialog] save error:", e);
      toast({ title: "Couldn't save result", description: "Please try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm p-0 gap-0 overflow-hidden border-0 shadow-xl">
        <VisuallyHidden>
          <DialogTitle>Test WiFi speed</DialogTitle>
        </VisuallyHidden>

        <div className="relative bg-gradient-to-br from-primary/20 via-primary/10 to-transparent p-4 pb-3">
          <div className="flex items-center gap-3">
            <Mascot mood="happy" size="sm" />
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-lg font-semibold text-foreground">WiFi Speed Test</h3>
              <p className="text-[11px] text-muted-foreground truncate">{cafeName}</p>
            </div>
          </div>
        </div>

        <div className="p-5 min-h-[200px] flex flex-col justify-center">
          {phase === "checking" && (
            <div className="flex flex-col items-center justify-center gap-3">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {phase === "intro" && (
            <div className="space-y-4 text-center">
              <p className="text-sm text-foreground/90">
                Make sure you're connected to the café's WiFi before testing.
              </p>
              {error && (
                <p className="text-xs text-destructive">{error}</p>
              )}
              <Button onClick={handleStart} className="w-full" size="lg">
                <Wifi className="h-4 w-4 mr-2" />
                Start Test
              </Button>
            </div>
          )}

          {phase === "already_tested" && (
            <div className="text-center space-y-3">
              <div className="flex justify-center">
                <Mascot mood="happy" size="md" />
              </div>
              <h4 className="font-display text-xl font-semibold text-foreground">You're all set.</h4>
              <p className="text-sm text-foreground/80">
                You already tested the WiFi here recently — thanks for contributing.
              </p>
            </div>
          )}

          {phase === "running" && (
            <div className="flex flex-col items-center justify-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm font-medium">Testing WiFi speed…</p>
              <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {phase === "result" && result && (
            <div className="text-center space-y-3">
              <p className="text-4xl font-bold text-primary">{result.downloadSpeed} Mbps</p>
              <p className="text-xs text-muted-foreground">Latency: {result.latency}ms</p>
              {saving && (
                <p className="text-xs text-muted-foreground flex items-center justify-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" /> Saving…
                </p>
              )}
            </div>
          )}

          {phase === "saved" && result && (
            <div className="text-center space-y-3">
              <CheckCircle2 className="h-10 w-10 text-primary mx-auto" />
              <p className="text-3xl font-bold">{result.downloadSpeed} Mbps</p>
              <p className="text-sm text-foreground/90">Saved to this café's WiFi score.</p>
            </div>
          )}
        </div>

        <div className="p-4 pt-2 border-t bg-background">
          {phase === "saved" || phase === "result" || phase === "already_tested" ? (
            <Button
              onClick={handleClose}
              disabled={saving || isRunning}
              className="w-full"
              size="sm"
            >
              Done
            </Button>
          ) : (
            <Button
              onClick={handleClose}
              variant="ghost"
              className="w-full text-muted-foreground"
              size="sm"
              disabled={isRunning}
            >
              {isRunning ? "Testing…" : "Cancel"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
